import { CIAnalytics } from 'common/analytics/analytics.provider';
import { AuthActions } from 'common/auth/actions';
import {
    clearPersistedIdentity,
    deleteLastPagesOpened,
    login,
    revokeCognitoTokenAndSignOut,
    updateUsersLastLoggedInTime,
} from 'common/auth/helpers';
import type { SsoSessionKeys } from 'common/auth/sso/sso.types';
import type { ActiveStudySite, AuthUser } from 'common/auth/types';
import { setLanguage } from 'common/i18n/i18n';
import oktaAuth from 'module/auth/okta/oktaAuth';

import type { DeviceHandoffRejectionReason } from '../state/types';

import { logHandoffRejected, auditLogRespondentTookControl, auditLogSiteUserResumed } from './audit';

export async function authenticateIncomingHandoffUser(
    username: string | null,
    password: string | null,
    ssoTokens?: SsoSessionKeys
): Promise<AuthUser> {
    const authUser = await login(username, password, ssoTokens);
    await deleteLastPagesOpened(authUser);

    return updateUsersLastLoggedInTime(authUser);
}

export type AuthDispatch = (action: { type: string; payload?: unknown; meta?: unknown; error?: boolean }) => void;

export function acceptIncomingHandoffUser(
    dispatch: AuthDispatch,
    incomingUser: AuthUser,
    activeStudySite: ActiveStudySite,
    isSiteUserReturning: boolean
): void {
    setLanguage(incomingUser.preferredLanguage || 'en-US');
    CIAnalytics.getInstance().identifyUser(incomingUser);

    dispatch(AuthActions.setActiveUserFromHandoff(incomingUser, activeStudySite));

    if (isSiteUserReturning) {
        auditLogSiteUserResumed(incomingUser);
    } else {
        auditLogRespondentTookControl(incomingUser, activeStudySite);
    }
}

export async function rejectIncomingHandoffUser(
    rejectedUser: AuthUser,
    reason: DeviceHandoffRejectionReason
): Promise<void> {
    logHandoffRejected(rejectedUser, reason);

    if (rejectedUser.authenticationMethod === 'OKTA') {
        oktaAuth.tokenManager.clear();
    } else {
        await revokeCognitoTokenAndSignOut(rejectedUser);
    }

    clearPersistedIdentity();
}
